import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Download, OctagonAlert, Undo2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import {
  extractImportErrorMessage,
  triggerBlobDownload,
  useDownloadImportRejects,
  useImportJob,
  useImportRows,
  useUndoImport,
} from "@/api/import";
import { ImportTable } from "@/lib/import/import-types";

/**
 * Rapport final.
 *
 * Le numero de ligne affiche est celui de la feuille Excel, pas un index
 * interne : c'est ce qui permet au client de retrouver la ligne dans **son**
 * fichier et de la corriger.
 */
const PAGE_SIZE = 25;

export const ImportStepReport = ({
  jobId,
  table,
  onRestart,
}: {
  jobId: string;
  table: ImportTable;
  onRestart: () => void;
}) => {
  const vt = useVisibleT();
  const { toast } = useToast();
  const [page, setPage] = useState(0);
  const downloadRejects = useDownloadImportRejects();
  const undoImport = useUndoImport();

  const { data: job, isLoading } = useImportJob(jobId);
  const { data: failed } = useImportRows(jobId, {
    status: "failed",
    take: PAGE_SIZE,
    skip: page * PAGE_SIZE,
  });

  if (isLoading || !job) {
    return (
      <div className="flex flex-col gap-3">
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  const hasErrors = job.errorCount > 0;
  const cancelled = job.status === "cancelled";
  const failedTotal = failed?.total ?? 0;
  const undone = !!job.undoneAt;
  const canUndo = !undone && job.createdCount > 0;

  const runUndo = () => {
    undoImport.mutate(jobId, {
      onSuccess: (result) => {
        const parts = [
          `${result.deleted} ${vt("bo.import.undo.deleted", "fiche(s) supprimée(s)")}`,
        ];
        if (result.notRevertible > 0) {
          parts.push(
            `${result.notRevertible} ${vt(
              "bo.import.undo.notRevertible",
              "mise(s) à jour non rétablie(s)",
            )}`,
          );
        }
        if (result.failed.length > 0) {
          parts.push(
            `${result.failed.length} ${vt("bo.import.undo.failed", "en échec")}`,
          );
        }
        toast({
          variant: result.undone ? "default" : "destructive",
          title: result.undone
            ? vt("bo.import.undo.done", "Import annulé")
            : vt("bo.import.undo.partial", "Annulation partielle"),
          description: parts.join(" · "),
        });
      },
      onError: (error) => {
        const { message } = extractImportErrorMessage(error);
        toast({
          variant: "destructive",
          title:
            message ??
            vt("bo.import.undo.error", "Impossible d'annuler cet import."),
        });
      },
    });
  };

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          {hasErrors || cancelled ? (
            <OctagonAlert className="w-5 h-5 text-amber-600" aria-hidden />
          ) : (
            <CheckCircle2 className="w-5 h-5 text-green-600" aria-hidden />
          )}
          <h3 className="text-base font-semibold">
            {cancelled
              ? vt("bo.import.report.cancelled", "Import annulé")
              : hasErrors
                ? vt("bo.import.report.doneWithErrors", "Import terminé avec des erreurs")
                : vt("bo.import.report.done", "Import terminé")}
          </h3>
          <Badge variant="secondary" className="font-normal">
            {job.fileName}
          </Badge>
        </div>
        <p className="text-sm text-gray-600">
          {vt("bo.import.report.mode", "Mode")} :{" "}
          {job.writeMode === "createAndUpdate"
            ? vt("bo.import.confirm.createAndUpdate", "Créer et mettre à jour")
            : vt("bo.import.confirm.createOnly", "Créer uniquement")}
        </p>
      </header>

      <dl className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          {
            label: vt("bo.import.running.created", "Créées"),
            value: job.createdCount,
            tone: "text-green-700",
          },
          {
            label: vt("bo.import.running.updated", "Mises à jour"),
            value: job.updatedCount,
            tone: "text-gray-900",
          },
          {
            label: vt("bo.import.running.skipped", "Ignorées"),
            value: job.skippedCount,
            tone: "text-gray-500",
          },
          {
            label: vt("bo.import.running.failed", "Échecs"),
            value: job.errorCount,
            tone: job.errorCount > 0 ? "text-red-700" : "text-gray-500",
          },
        ].map((counter) => (
          <div key={counter.label} className="flex flex-col">
            <dt className="text-xs text-gray-500">{counter.label}</dt>
            <dd className={`text-2xl font-semibold tabular-nums ${counter.tone}`}>
              {counter.value.toLocaleString("fr-FR")}
            </dd>
          </div>
        ))}
      </dl>

      {(() => {
        const byType = Object.entries(job.linkedCreatedByType ?? {}).filter(
          ([, count]) => count > 0,
        );
        if (byType.length === 0) return null;
        const labelOf = (type: string) =>
          ({
            author: vt("bo.import.report.createdAuthors", "auteur(s)"),
            collector: vt("bo.import.report.createdCollectors", "collectionneur(s)"),
            ensemble: vt("bo.import.report.createdEnsembles", "ensemble(s)"),
          })[type] ?? type;
        return (
          <p className="text-sm text-blue-800 p-3 rounded-xl border border-blue-200 bg-blue-50">
            {vt("bo.import.report.linkedCreated", "Fiches liées créées")} :{" "}
            {byType
              .map(([type, count]) => `${count} ${labelOf(type)}`)
              .join(" · ")}
          </p>
        );
      })()}

      {job.errorMessage && (
        <p className="text-sm text-red-800 p-3 rounded-xl border border-red-200 bg-red-50">
          {job.errorMessage}
        </p>
      )}

      {undone && (
        <p className="flex items-center gap-2 text-sm text-gray-700 p-3 rounded-xl border bg-gray-50">
          <Undo2 className="w-4 h-4 shrink-0" aria-hidden />
          {vt(
            "bo.import.undo.banner",
            "Cet import a été annulé : les fiches créées ont été supprimées. Les mises à jour n'ont pas été rétablies.",
          )}
        </p>
      )}

      {failedTotal > 0 && (
        <>
          <Separator />
          <section className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-3">
              <h4 className="text-sm font-semibold">
                {vt("bo.import.report.errorsTitle", "Lignes non importées")}
              </h4>
              <Button
                size="sm"
                variant="outline"
                disabled={downloadRejects.isPending}
                onClick={() =>
                  downloadRejects.mutate(jobId, {
                    onSuccess: (response) =>
                      triggerBlobDownload(response, `${job.fileName || "import"}-rejets.xlsx`),
                  })
                }
              >
                <Download className="w-4 h-4 mr-1.5" aria-hidden />
                {vt(
                  "bo.import.report.downloadRejects",
                  "Télécharger les lignes à corriger (.xlsx)",
                )}
              </Button>
            </div>
            <p className="text-xs text-gray-500">
              {vt(
                "bo.import.report.rejectsHint",
                "Le fichier reprend vos colonnes et une colonne « Erreur ». Corrigez-le puis redéposez-le : seules ces lignes seront réimportées.",
              )}
            </p>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[10%]">
                    {vt("bo.import.report.row", "Ligne")}
                  </TableHead>
                  <TableHead className="w-[22%]">
                    {vt("bo.import.report.key", "N° d'enregistrement")}
                  </TableHead>
                  <TableHead>
                    {vt("bo.import.report.problem", "Problème")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {failed?.items.map((row) => (
                  <TableRow key={row.rowIndex}>
                    <TableCell className="tabular-nums align-top">
                      {row.rowIndex}
                    </TableCell>
                    <TableCell className="align-top font-mono text-xs">
                      {row.matchKey ?? "—"}
                    </TableCell>
                    <TableCell className="align-top">
                      <ul className="flex flex-col gap-0.5">
                        {(row.issues ?? [])
                          .filter((issue) => issue.level === "error")
                          .map((issue, index) => (
                            <li key={`${issue.code}-${index}`} className="text-sm">
                              {issue.field && (
                                <span className="text-gray-500">
                                  {issue.field} —{" "}
                                </span>
                              )}
                              {issue.message}
                            </li>
                          ))}
                      </ul>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {failedTotal > PAGE_SIZE && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500 tabular-nums">
                  {page * PAGE_SIZE + 1}–
                  {Math.min((page + 1) * PAGE_SIZE, failedTotal)}{" "}
                  {vt("bo.import.decisions.of", "sur")} {failedTotal}
                </span>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={page === 0}
                    onClick={() => setPage((current) => current - 1)}
                  >
                    {vt("bo.import.previous", "Précédent")}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={(page + 1) * PAGE_SIZE >= failedTotal}
                    onClick={() => setPage((current) => current + 1)}
                  >
                    {vt("bo.import.next", "Suivant")}
                  </Button>
                </div>
              </div>
            )}
          </section>
        </>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t">
        <Button variant="outline" onClick={onRestart}>
          {vt("bo.import.report.newImport", "Nouvel import")}
        </Button>
        <div className="flex items-center gap-2">
          {canUndo && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" className="text-red-700 hover:text-red-800">
                  <Undo2 className="w-4 h-4 mr-1.5" aria-hidden />
                  {vt("bo.import.undo.button", "Annuler l'import")}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    {vt("bo.import.undo.confirmTitle", "Annuler cet import ?")}
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {`${job.createdCount.toLocaleString("fr-FR")} `}
                    {vt(
                      "bo.import.undo.confirmBody",
                      "fiche(s) créée(s) par cet import seront supprimées. Cette action est irréversible. Les fiches mises à jour ne seront pas rétablies.",
                    )}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>
                    {vt("bo.import.undo.cancel", "Retour")}
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={runUndo}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    {vt("bo.import.undo.confirm", "Supprimer les fiches créées")}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          {!undone && job.createdCount + job.updatedCount > 0 && (
            <Button asChild>
              <Link to={`/dashboard/${table}`}>
                {vt("bo.import.report.seeRecords", "Voir les fiches")}
              </Link>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
